/**
 * Release Board filter bar.
 *
 * Desktop: search + one compact menu per filter. Mobile: search + a "Filters"
 * button that opens a bottom sheet with the same controls. Active filters show
 * as removable chips with a "Clear all". State lives in the URL (see
 * useReleaseBoardFilters); this component is purely controlled.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { useTranslation } from 'react-i18next';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { SelectChevron } from '@/components/ui/Select';
import { filterTriggerHover } from '@/components/ui/filterTriggerStyles';
import { ICONS } from '@/constants/icons';
import { MEDIA_KIND_VISUALS, type MediaKind } from '@/constants/mediaKinds';
import { useDebounce } from '@/hooks/useDebounce';
import { BOARD_SEARCH_MAX_LENGTH } from '@/types/releaseBoard';
import { useBoardFacets } from '@/hooks/useReleaseBoard';
import { isSupportedLanguage, languageDisplayName } from '@/utils/languages';
import { cn } from '@/utils/cn';
import {
  BOARD_MEDIA_TYPE_VALUES,
  BOARD_NEEDED_BY_VALUES,
  BOARD_LANG_VERSION_PREFIX,
  BOARD_VERSION_VALUES,
  versionFilterLanguage,
  type BoardFacets,
  type BoardFilters,
  type BoardMediaTypeFilter,
  type BoardNeededByFilter,
  type BoardVersionFilter,
} from '@/types/releaseBoard';

const SEARCH_DEBOUNCE_MS = 300;

interface FilterOption {
  value: string;
  label: string;
  /** Small leading visual (icon / initials), decorative */
  icon?: ReactNode;
}

/** Decorative glyph for option rows, pills and chips, always in the same fixed-width centered box. */
const OptionGlyph = ({ name, glyph, tone = 'text-gray-600' }: { name?: string; glyph?: string; tone?: string }) => (
  <span className={cn('inline-flex h-4 w-4 flex-shrink-0 items-center justify-center text-sm leading-none', tone)} aria-hidden="true">
    {name ? (
      <Icon name={name} size="custom" color={tone} className="text-[14px]" ariaHidden />
    ) : (
      <span className="text-[11px] leading-none">{glyph}</span>
    )}
  </span>
);

/** Count pill used on filter buttons (same size/centering everywhere). */
const CountBadge = ({ count }: { count: number }) => (
  <span
    aria-hidden="true"
    className="inline-flex h-4 min-w-[1rem] flex-shrink-0 items-center justify-center rounded-full bg-primary-600 px-1 text-[10px] font-semibold leading-none text-primary-600-contrast"
  >
    {count}
  </span>
);

/** Option glyph for a media kind, from the shared Creator-table mapping. */
const MediaKindGlyph = ({ kind }: { kind: MediaKind }) => (
  <OptionGlyph name={MEDIA_KIND_VISUALS[kind].icon} glyph={MEDIA_KIND_VISUALS[kind].glyph} />
);

/** Values of `options` that equal one of `names` case-insensitively (selected names not in the list stay selected). */
function optionValuesMatching(options: FilterOption[], names: readonly string[]): string[] {
  return names.map((n) => options.find((o) => o.value.toLowerCase() === n.toLowerCase())?.value ?? n);
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter((p) => p.length > 0);
  if (parts.length === 0) return '?';
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
  return (first + last).toUpperCase();
}

const InitialsAvatar = ({ name }: { name: string }) => (
  <span
    aria-hidden="true"
    className="inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-gray-200 text-[8px] font-semibold leading-none text-gray-600"
  >
    {initialsOf(name)}
  </span>
);

export type BoardMenuKey = 'creator' | 'labels' | 'version' | 'needed' | 'media';

/**
 * SSOT for which filter menus exist (desktop bar AND mobile sheet). A menu needs at least two meaningful
 * options on this board (a filter that can only ever select everything is noise); a version menu needs
 * versions, a needed-by menu needs a dated item. A currently ACTIVE filter always keeps its menu so it can
 * be seen and cleared. Until the facets load, only active filters show (no flicker of menus that then vanish).
 */
export function visibleBoardMenus(facets: BoardFacets | undefined, filters: BoardFilters): ReadonlySet<BoardMenuKey> {
  const menus = new Set<BoardMenuKey>();
  if (facets) {
    if (facets.creators.length >= 2) menus.add('creator');
    if (facets.labels.length >= 2) menus.add('labels');
    if (facets.hasVariations || facets.languages.length >= 2) menus.add('version');
    if (facets.hasNeededBy) menus.add('needed');
    if (facets.mediaKinds.length >= 2) menus.add('media');
  }
  if (filters.creatorId !== null) menus.add('creator');
  if (filters.labels.length > 0) menus.add('labels');
  if (filters.version !== 'all') menus.add('version');
  if (filters.neededBy) menus.add('needed');
  if (filters.mediaType) menus.add('media');
  return menus;
}

interface BoardFilterBarProps {
  filters: BoardFilters;
  activeCount: number;
  onChange: (patch: Partial<BoardFilters>) => void;
  onClearAll: () => void;
}

// ---------------------------------------------------------------------------
// Option list (shared by the desktop popovers and the mobile sheet)
// ---------------------------------------------------------------------------

interface OptionListProps {
  label: string;
  options: FilterOption[];
  selected: readonly string[];
  multi?: boolean;
  searchPlaceholder?: string;
  emptyText: string;
  onToggle: (value: string) => void;
  /** Render short lists as wrapping pills (mobile sheet) instead of rows */
  pills?: boolean;
}

const OptionList = ({
  label,
  options,
  selected,
  multi = false,
  searchPlaceholder,
  emptyText,
  onToggle,
  pills = false,
}: OptionListProps) => {
  const [search, setSearch] = useState('');
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;
  }, [options, search]);

  return (
    <div className="flex flex-col gap-1.5 min-h-0">
      {searchPlaceholder && options.length > 6 && (
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          className="h-8 w-full rounded-lg border border-gray-200 bg-white px-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-primary-500 transition-colors"
        />
      )}
      {visible.length === 0 ? (
        <p className="px-2 py-3 text-xs text-gray-500">{emptyText}</p>
      ) : (
        <div
          role="listbox"
          aria-label={label}
          aria-multiselectable={multi}
          className={cn(
            pills ? 'flex flex-wrap gap-1.5' : 'flex flex-col gap-0.5 overflow-y-auto max-h-56'
          )}
        >
          {visible.map((option) => {
            const isSelected = selected.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => onToggle(option.value)}
                className={cn(
                  'flex items-center justify-between gap-2 border text-left text-sm',
                  'transition-colors focus:outline-none focus-visible:border-primary-600',
                  pills
                    ? 'rounded-full px-3 py-1.5'
                    : 'rounded-lg border-transparent px-2.5 py-1.5',
                  pills && !isSelected && 'border-gray-200 bg-white',
                  pills && isSelected && 'border-primary-300',
                  isSelected
                    ? 'bg-primary-50 text-primary-800 font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                )}
              >
                <span className="flex min-w-0 items-center gap-2">
                  {option.icon}
                  <span className="truncate">{option.label}</span>
                </span>
                {isSelected && <Icon name={ICONS.CHECK} size="xs" color="text-primary-600" ariaHidden />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Desktop popover trigger
// ---------------------------------------------------------------------------

interface FilterMenuProps extends OptionListProps {
  title: string;
  /** Current selection text (shown as a chip below the bar), null when nothing is selected */
  summary: string | null;
}

const FilterMenu = ({ title, summary, ...list }: FilterMenuProps) => {
  const isActive = summary !== null;
  // Badge shares the chip rule (`summary`): a default selection ('all') has no chip, so no badge.
  const badgeCount = isActive ? list.selected.length : 0;
  return (
    <PopoverPrimitive.Root>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex h-8 max-w-[220px] items-center gap-1.5 rounded-full border px-3 text-xs font-medium whitespace-nowrap',
            'transition-colors duration-200 focus:outline-none focus-visible:border-primary-600',
            isActive
              ? 'border-primary-300 bg-primary-50 text-primary-800'
              : cn('border-gray-200 bg-white text-gray-700', filterTriggerHover)
          )}
        >
          <span className="truncate">{title}</span>
          {badgeCount > 0 && <CountBadge count={badgeCount} />}
          <SelectChevron className="flex-shrink-0" />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          className="z-popover-top w-60 rounded-xl border border-gray-200 bg-white p-2 shadow-lg"
        >
          <OptionList {...list} />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
};

// ---------------------------------------------------------------------------
// Chips
// ---------------------------------------------------------------------------

interface ActiveChip {
  key: string;
  icon?: ReactNode;
  text: string;
  onRemove: () => void;
}

const FilterChip = ({ chip }: { chip: ActiveChip }) => {
  const { t } = useTranslation('common');
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 pl-2.5 pr-1 py-0.5 text-xs font-medium text-primary-800">
      {chip.icon}
      <span className="max-w-[200px] truncate">{chip.text}</span>
      <button
        type="button"
        onClick={chip.onRemove}
        aria-label={t('release_filters_remove', { name: chip.text })}
        className="relative inline-flex h-4 w-4 items-center justify-center rounded-full border border-transparent text-primary-700 max-sm:before:absolute max-sm:before:-inset-[14px] max-sm:before:content-[''] transition-colors hover:bg-primary-100 focus:outline-none focus-visible:border-primary-600"
      >
        <Icon name={ICONS.CLOSE} size="custom" className="text-[10px]" ariaHidden />
      </button>
    </span>
  );
};

// ---------------------------------------------------------------------------
// Bar
// ---------------------------------------------------------------------------

export const BoardFilterBar = ({ filters, activeCount, onChange, onClearAll }: BoardFilterBarProps) => {
  const { t } = useTranslation('common');
  const { facets } = useBoardFacets();
  const people = facets?.creators ?? [];
  const labels = facets?.labels ?? [];
  const languages = facets?.languages ?? [];
  const activeLanguage = versionFilterLanguage(filters.version);
  const visibleMenus = visibleBoardMenus(facets, filters);
  const mediaPresent = facets?.mediaKinds ?? [];
  const [sheetOpen, setSheetOpen] = useState(false);

  // --- Search: local text, debounced into the URL ----------------------------
  const [text, setText] = useState(filters.q);
  const debounced = useDebounce(text, SEARCH_DEBOUNCE_MS);
  const lastPushedRef = useRef(filters.q);
  useEffect(() => {
    if (debounced.trim() === filters.q.trim()) return;
    lastPushedRef.current = debounced;
    onChange({ q: debounced });
    // onChange is stable per URL change; only react to the debounced text.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);
  useEffect(() => {
    // URL changed from outside (chip removed, clear all, link): follow it.
    if (filters.q.trim() !== lastPushedRef.current.trim()) {
      lastPushedRef.current = filters.q;
      setText(filters.q);
    }
  }, [filters.q]);

  // --- Options ----------------------------------------------------------------
  const creatorOptions = useMemo<FilterOption[]>(
    () => people.map((p) => ({ value: String(p.id), label: p.name, icon: <InitialsAvatar name={p.name} /> })),
    [people]
  );
  const labelOptions = useMemo<FilterOption[]>(
    () => labels.map((name) => ({ value: name, label: name, icon: <OptionGlyph name={ICONS.TAG} /> })),
    [labels]
  );
  const versionOptions: FilterOption[] = [
    { value: 'all', label: t('release_filters_version_all'), icon: <OptionGlyph name={ICONS.LAYER} /> },
    // Original / all variations only mean something when this board has versions (or one is already selected).
    ...(facets?.hasVariations || filters.version === 'original' || filters.version === 'variations'
      ? [
          { value: 'original', label: t('release_filters_version_original'), icon: <OptionGlyph name={ICONS.GLOBE} /> },
          { value: 'variations', label: t('release_filters_version_variations'), icon: <OptionGlyph name={ICONS.LAYER} /> },
        ]
      : []),
    // One entry per language present on this board (server facets); the active one stays so it can be cleared.
    ...(activeLanguage && !languages.includes(activeLanguage) ? [...languages, activeLanguage] : languages)
      .filter(isSupportedLanguage)
      .map(
      (code): FilterOption => ({
        value: `${BOARD_LANG_VERSION_PREFIX}${code}`,
        label: languageDisplayName(code),
        icon: <OptionGlyph name={ICONS.LANGUAGE} />,
      })
    ),
  ];
  const neededOptions: FilterOption[] = [
    { value: 'overdue', label: t('release_filters_needed_overdue'), icon: <OptionGlyph name={ICONS.CLOCK} tone="text-red-600" /> },
    { value: 'today', label: t('release_filters_needed_today'), icon: <OptionGlyph name={ICONS.CALENDAR} /> },
    { value: 'week', label: t('release_filters_needed_week'), icon: <OptionGlyph name={ICONS.CALENDAR_WEEK} /> },
    { value: 'none', label: t('release_filters_needed_none'), icon: <OptionGlyph name={ICONS.MINUS} /> },
  ];
  const allMediaOptions: FilterOption[] = [
    { value: 'image', label: t('release_filters_media_image'), icon: <MediaKindGlyph kind="image" /> },
    { value: 'video', label: t('release_filters_media_video'), icon: <MediaKindGlyph kind="video" /> },
    { value: 'document', label: t('release_filters_media_document'), icon: <MediaKindGlyph kind="document" /> },
    { value: 'carousel', label: t('release_filters_media_carousel'), icon: <MediaKindGlyph kind="carousel" /> },
    { value: 'text', label: t('release_filters_media_text'), icon: <MediaKindGlyph kind="text" /> },
  ];

  // Only the kinds this board has (the active one stays so it can be cleared).
  const mediaOptions = allMediaOptions.filter(
    (o) => mediaPresent.some((k) => k === o.value) || o.value === filters.mediaType
  );

  const labelOf = (options: FilterOption[], value: string | null): string | null =>
    options.find((o) => o.value === value)?.label ?? null;

  const creatorName = filters.creatorId !== null
    ? labelOf(creatorOptions, String(filters.creatorId)) ?? `#${filters.creatorId}`
    : null;
  const selectedLabelNames = filters.labels;
  const versionName = filters.version !== 'all' ? labelOf(versionOptions, filters.version) : null;
  const neededName = labelOf(neededOptions, filters.neededBy);
  const mediaName = labelOf(mediaOptions, filters.mediaType);

  // --- Handlers ---------------------------------------------------------------
  const toggleCreator = (value: string): void =>
    onChange({ creatorId: filters.creatorId === Number(value) ? null : Number(value) });
  const toggleLabel = (value: string): void => {
    const isOn = filters.labels.some((l) => l.toLowerCase() === value.toLowerCase());
    onChange({
      labels: isOn
        ? filters.labels.filter((l) => l.toLowerCase() !== value.toLowerCase())
        : [...filters.labels, value],
    });
  };
  const pickVersion = (value: string): void =>
    onChange({
      version:
        BOARD_VERSION_VALUES.find((v) => v === value) ??
        (value.startsWith(BOARD_LANG_VERSION_PREFIX) ? (value as BoardVersionFilter) : 'all'),
    });
  const toggleNeeded = (value: string): void => {
    const next: BoardNeededByFilter | undefined = BOARD_NEEDED_BY_VALUES.find((v) => v === value);
    onChange({ neededBy: next && next !== filters.neededBy ? next : null });
  };
  const toggleMedia = (value: string): void => {
    const next: BoardMediaTypeFilter | undefined = BOARD_MEDIA_TYPE_VALUES.find((v) => v === value);
    onChange({ mediaType: next && next !== filters.mediaType ? next : null });
  };

  // --- Chips ------------------------------------------------------------------
  const chips: ActiveChip[] = [];
  if (filters.q.trim()) {
    chips.push({
      key: 'q',
      text: t('release_filters_chip_search', { q: filters.q.trim() }),
      onRemove: () => onChange({ q: '' }),
    });
  }
  if (creatorName) {
    chips.push({
      key: 'creator',
      icon: creatorName ? <InitialsAvatar name={creatorName} /> : undefined,
      text: t('release_filters_chip', { filter: t('release_filters_creator'), value: creatorName }),
      onRemove: () => onChange({ creatorId: null }),
    });
  }
  filters.labels.forEach((name) => {
    chips.push({
      key: `label-${name}`,
      icon: <OptionGlyph name={ICONS.TAG} tone="text-primary-700" />,
      text: t('release_filters_chip', { filter: t('release_filters_labels'), value: name }),
      onRemove: () => onChange({ labels: filters.labels.filter((l) => l !== name) }),
    });
  });
  if (versionName) {
    chips.push({
      key: 'version',
      icon: versionOptions.find((o) => o.value === filters.version)?.icon,
      text: t('release_filters_chip', { filter: t('release_filters_version'), value: versionName }),
      onRemove: () => onChange({ version: 'all' }),
    });
  }
  if (neededName) {
    chips.push({
      key: 'needed',
      icon: neededOptions.find((o) => o.value === filters.neededBy)?.icon,
      text: t('release_filters_chip', { filter: t('release_filters_needed_by'), value: neededName }),
      onRemove: () => onChange({ neededBy: null }),
    });
  }
  if (mediaName) {
    chips.push({
      key: 'media',
      icon: mediaOptions.find((o) => o.value === filters.mediaType)?.icon,
      text: t('release_filters_chip', { filter: t('release_filters_media'), value: mediaName }),
      onRemove: () => onChange({ mediaType: null }),
    });
  }

  const emptyText = t('release_filters_no_options');
  const listProps = {
    creator: {
      label: t('release_filters_creator'),
      options: creatorOptions,
      selected: filters.creatorId !== null ? [String(filters.creatorId)] : [],
      searchPlaceholder: t('release_filters_creator_search'),
      emptyText,
      onToggle: toggleCreator,
    },
    labels: {
      label: t('release_filters_labels'),
      options: labelOptions,
      selected: optionValuesMatching(labelOptions, filters.labels),
      multi: true,
      searchPlaceholder: t('release_filters_labels_search'),
      emptyText,
      onToggle: toggleLabel,
    },
    version: {
      label: t('release_filters_version'),
      options: versionOptions,
      selected: [filters.version],
      emptyText,
      onToggle: pickVersion,
    },
    needed: {
      label: t('release_filters_needed_by'),
      options: neededOptions,
      selected: filters.neededBy ? [filters.neededBy] : [],
      emptyText,
      onToggle: toggleNeeded,
    },
    media: {
      label: t('release_filters_media'),
      options: mediaOptions,
      selected: filters.mediaType ? [filters.mediaType] : [],
      emptyText,
      onToggle: toggleMedia,
    },
  } as const;

  const labelsSummary =
    selectedLabelNames.length === 0
      ? null
      : selectedLabelNames.length === 1
        ? selectedLabelNames[0]
        : `${selectedLabelNames[0]} +${selectedLabelNames.length - 1}`;

  return (
    <div className="space-y-2" role="search">
      <div className="flex flex-wrap items-center gap-2">
        {/* Search */}
        <div className="relative min-w-[160px] flex-1 lg:max-w-xs lg:flex-none lg:w-64">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <Icon name={ICONS.SEARCH} size="xs" ariaHidden />
          </span>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('release_filters_search_placeholder')}
            aria-label={t('release_filters_search_aria')}
            maxLength={BOARD_SEARCH_MAX_LENGTH}
            className="h-8 w-full rounded-full border border-gray-200 bg-white pl-8 pr-3 text-sm text-gray-900 placeholder:text-gray-400 transition-colors focus:outline-none focus:border-primary-500"
          />
        </div>

        {/* Desktop menus */}
        {visibleMenus.size > 0 && (
          <div className="hidden lg:flex flex-wrap items-center gap-2">
            {visibleMenus.has('creator') && (
              <FilterMenu title={t('release_filters_creator')} summary={creatorName} {...listProps.creator} />
            )}
            {visibleMenus.has('labels') && (
              <FilterMenu title={t('release_filters_labels')} summary={labelsSummary} {...listProps.labels} />
            )}
            {visibleMenus.has('version') && (
              <FilterMenu title={t('release_filters_version')} summary={versionName} {...listProps.version} />
            )}
            {visibleMenus.has('needed') && (
              <FilterMenu title={t('release_filters_needed_by')} summary={neededName} {...listProps.needed} />
            )}
            {visibleMenus.has('media') && (
              <FilterMenu title={t('release_filters_media')} summary={mediaName} {...listProps.media} />
            )}
          </div>
        )}

        {/* Mobile: open the sheet */}
        {visibleMenus.size > 0 && (
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          aria-label={
            activeCount > 0
              ? t('release_filters_button_active', { count: activeCount })
              : t('release_filters_title')
          }
          className={cn(
            'lg:hidden inline-flex h-[44px] sm:h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium',
            'transition-colors focus:outline-none focus-visible:border-primary-600',
            activeCount > 0
              ? 'border-primary-300 bg-primary-50 text-primary-800'
              : 'border-gray-200 bg-white text-gray-700'
          )}
        >
          <Icon name={ICONS.FILTER} size="xs" ariaHidden />
          <span>{t('release_filters_title')}</span>
          {activeCount > 0 && <CountBadge count={activeCount} />}
        </button>
        )}
      </div>

      {/* Active chips */}
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {chips.map((chip) => (
            <FilterChip key={chip.key} chip={chip} />
          ))}
          <button
            type="button"
            onClick={onClearAll}
            className="rounded-full border border-transparent px-2 py-0.5 max-sm:min-h-[44px] text-xs font-medium text-gray-600 underline-offset-2 transition-colors hover:text-primary-700 hover:underline focus:outline-none focus-visible:border-primary-600"
          >
            {t('release_filters_clear_all')}
          </button>
        </div>
      )}

      {/* Mobile sheet */}
      <BottomSheet open={sheetOpen} onOpenChange={setSheetOpen} maxHeight="85dvh">
        <div className="flex min-h-full flex-col">
          <h2 className="px-4 pt-4 pb-2 pr-14 text-base font-semibold text-gray-900">
            {t('release_filters_title')}
          </h2>
          <div className="flex-1 space-y-5 px-4 pb-4">
            {(
              [
                ['creator', t('release_filters_creator'), false],
                ['labels', t('release_filters_labels'), false],
                ['version', t('release_filters_version'), true],
                ['needed', t('release_filters_needed_by'), true],
                ['media', t('release_filters_media'), true],
              ] as const
            )
              .filter(([key]) => visibleMenus.has(key))
              .map(([key, title, pills]) => (
              <section key={key} className="space-y-1.5">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</h3>
                <OptionList {...listProps[key]} pills={pills} />
              </section>
            ))}
          </div>
          <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-gray-100 bg-white px-4 py-3">
            <Button variant="secondary" size="sm" onClick={onClearAll} disabled={activeCount === 0}>
              {t('release_filters_clear_all')}
            </Button>
            <Button variant="primary" size="sm" onClick={() => setSheetOpen(false)}>
              {t('release_filters_done')}
            </Button>
          </div>
        </div>
      </BottomSheet>
    </div>
  );
};

export default BoardFilterBar;
